import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ImagePlus, Loader2, Plus, Save, Sparkles, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, useAdminApi } from "@/lib/api";

const KEYS = [
  ["title", "Title", false],
  ["tagline", "Tagline / slogan", false],
  ["intro", "Short intro", true],
  ["vision", "Vision line", true],
  ["opportunities_heading", "Opportunities heading", false],
  ["opportunities", "Opportunities (one per line)", true],
  ["join_consent", "Join consent text", true],
  ["cancel_consent", "Cancel consent text", true],
];

export default function ForumTab() {
  const call = useAdminApi();
  const [cfg, setCfg] = useState(null);
  const [members, setMembers] = useState(null);
  const [password, setPassword] = useState("");
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    call("get", "/admin/forum/config").then(setCfg);
    call("get", "/admin/forum/members", null, { params: { include_cancelled: includeCancelled } }).then(setMembers);
  }, [call, includeCancelled]);
  useEffect(() => { load(); }, [load]);

  if (!cfg || !members) return <div className="h-32 animate-pulse rounded-xl bg-white/5" />;

  const saveText = (k, v) => setCfg({ ...cfg, texts: { ...cfg.texts, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const body = { texts: cfg.texts, whatsapp_group_link: cfg.whatsapp_group_link };
      if (password.trim()) body.password = password.trim();
      const next = await call("put", "/admin/forum/config", body);
      setCfg(next); setPassword("");
      toast.success("Forum settings saved");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the forum?`)) return;
    try {
      await call("delete", `/admin/forum/members/${m.id}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-forum">
      <section className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4">
        <h3 className="font-display text-lg text-white">Join password</h3>
        <p className="text-xs text-emerald-100/60">{cfg.has_password ? "A password is set. Enter a new one here to change it, or leave blank to keep it." : "No password is set yet. New members cannot join until you set one."}</p>
        <Input data-testid="forum-admin-password" type="password" placeholder="Set / change password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-black/20" />
      </section>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4">
        <h3 className="font-display text-lg text-white">WhatsApp group invite link</h3>
        <Input data-testid="forum-admin-wa-link" value={cfg.whatsapp_group_link || ""} onChange={(e) => setCfg({ ...cfg, whatsapp_group_link: e.target.value })} placeholder="https://chat.whatsapp.com/..." className="h-11 bg-black/20" />
      </section>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4">
        <h3 className="font-display text-lg text-white">Forum page text</h3>
        {KEYS.map(([k, label, multi]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-emerald-100/80">{label}</Label>
            {multi
              ? <Textarea data-testid={`forum-admin-${k}`} rows={k === "opportunities" ? 6 : 3} value={cfg.texts?.[k] || ""} onChange={(e) => saveText(k, e.target.value)} className="bg-black/20" />
              : <Input data-testid={`forum-admin-${k}`} value={cfg.texts?.[k] || ""} onChange={(e) => saveText(k, e.target.value)} className="h-11 bg-black/20" />}
          </div>
        ))}
      </section>

      <Button data-testid="forum-admin-save" onClick={save} disabled={saving} className="h-11 rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save forum settings</>}
      </Button>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-black/20 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-lg text-white">Members ({members.length})</h3>
          <div className="flex items-center gap-2 text-xs text-emerald-100/70">
            <Switch data-testid="forum-admin-include-cancelled" checked={includeCancelled} onCheckedChange={setIncludeCancelled} /> Include cancelled
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Designation</TableHead><TableHead>Joined</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} data-testid={`forum-admin-member-${m.id}`}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.designation_label}</TableCell>
                  <TableCell className="whitespace-nowrap">{(m.joined_at || "").slice(0, 10)}</TableCell>
                  <TableCell>{m.cancelled_at ? "Cancelled" : "Active"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => removeMember(m)} data-testid={`forum-admin-remove-${m.id}`} className="h-7 rounded-full text-red-200 hover:bg-red-500/15">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {members.length === 0 && <p className="py-6 text-center text-sm text-emerald-100/60" data-testid="forum-admin-empty">No members yet.</p>}
        </div>
      </section>

      <ActivitiesAdmin />
    </div>
  );
}

const todayIso = () => {
  const now = new Date();
  const tz = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return tz.toISOString().slice(0, 10);
};

const BLANK = { date: "", title_en: "", title_ml: "", description_en: "", description_ml: "" };

function ActivityRow({ a, onSaved, onDeleted }) {
  const call = useAdminApi();
  const inp = useRef(null);
  const [draft, setDraft] = useState(a);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(a);

  const save = async () => {
    setBusy(true);
    try {
      const next = await call("put", `/admin/forum/activities/${a.id}`, {
        title_en: draft.title_en.trim(), title_ml: draft.title_ml || "",
        description_en: draft.description_en || "", description_ml: draft.description_ml || "",
        date: draft.date,
      });
      onSaved(next);
      toast.success("Activity updated");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${a.title_en}" and all its photos / videos?`)) return;
    try { await call("delete", `/admin/forum/activities/${a.id}`); onDeleted(a.id); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await call("post", `/admin/forum/activities/${a.id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const refreshed = await call("get", "/admin/forum/activities");
      onSaved(refreshed.find((x) => x.id === a.id));
      toast.success("Uploaded");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setUploading(false); }
  };

  const removeMedia = async (fileId) => {
    if (!window.confirm("Remove this photo / video?")) return;
    try {
      await call("delete", `/admin/forum/activities/${a.id}/media/${fileId}`);
      onSaved({ ...a, media: a.media.filter((m) => m.id !== fileId) });
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className={`space-y-3 rounded-2xl border p-4 ${a.is_upcoming ? "border-emerald-300/25 bg-emerald-400/[0.04]" : "border-amber-200/25 bg-amber-200/[0.04]"}`} data-testid={`activity-row-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${a.is_upcoming ? "bg-emerald-400/15 text-emerald-200" : "bg-amber-200/15 text-amber-100"}`}>
          {a.is_upcoming ? <CalendarDays className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
          {a.is_upcoming ? "Upcoming" : "Completed"}
        </span>
        <Input type="date" data-testid={`activity-date-${a.id}`} value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="h-9 w-40 bg-black/20" />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input data-testid={`activity-title-en-${a.id}`} placeholder="Title (English)" value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} className="bg-black/20" />
        <Input data-testid={`activity-title-ml-${a.id}`} placeholder="തലക്കെട്ട് (മലയാളം)" value={draft.title_ml} onChange={(e) => setDraft({ ...draft, title_ml: e.target.value })} className="bg-black/20" />
        <Textarea data-testid={`activity-desc-en-${a.id}`} placeholder="Description (English)" rows={3} value={draft.description_en} onChange={(e) => setDraft({ ...draft, description_en: e.target.value })} className="bg-black/20" />
        <Textarea data-testid={`activity-desc-ml-${a.id}`} placeholder="വിവരണം (മലയാളം)" rows={3} value={draft.description_ml} onChange={(e) => setDraft({ ...draft, description_ml: e.target.value })} className="bg-black/20" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy || !dirty || !draft.title_en.trim() || !draft.date} data-testid={`activity-save-${a.id}`} className="h-8 rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300 disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="mr-1 h-3 w-3" /> Save</>}
        </Button>
        {!a.is_upcoming && (
          <>
            <input ref={inp} type="file" accept="image/png,image/jpeg,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" data-testid={`activity-upload-input-${a.id}`} onChange={upload} />
            <Button size="sm" variant="secondary" onClick={() => inp.current?.click()} disabled={uploading} data-testid={`activity-upload-${a.id}`} className="h-8 rounded-full">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ImagePlus className="mr-1 h-3 w-3" /> Add photo / video</>}
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" onClick={remove} data-testid={`activity-delete-${a.id}`} className="h-8 rounded-full text-red-200 hover:bg-red-500/15">
          <Trash2 className="mr-1 h-3 w-3" /> Delete
        </Button>
      </div>
      {a.media?.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {a.media.map((m) => (
            <div key={m.id} className="relative overflow-hidden rounded-xl bg-black/40" data-testid={`activity-media-admin-${m.id}`}>
              {m.kind === "video"
                ? <video src={m.url} controls preload="metadata" className="h-28 w-full bg-black object-cover" />
                : <img src={m.url} alt="" loading="lazy" className="h-28 w-full object-cover" />}
              <button type="button" onClick={() => removeMedia(m.id)} data-testid={`activity-media-remove-${m.id}`}
                      className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-red-500/80">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivitiesAdmin() {
  const call = useAdminApi();
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState({ ...BLANK, date: todayIso() });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    call("get", "/admin/forum/activities").then(setItems);
  }, [call]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.title_en.trim() || !draft.date) return toast.error("Date and English title are required");
    setCreating(true);
    try {
      await call("post", "/admin/forum/activities", {
        title_en: draft.title_en.trim(), title_ml: draft.title_ml || "",
        description_en: draft.description_en || "", description_ml: draft.description_ml || "",
        date: draft.date,
      });
      setDraft({ ...BLANK, date: todayIso() });
      load();
      toast.success("Activity added");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setCreating(false); }
  };

  const onSaved = (next) => setItems((xs) => xs.map((x) => (x.id === next.id ? next : x)));
  const onDeleted = (id) => setItems((xs) => xs.filter((x) => x.id !== id));

  if (!items) return <div className="h-32 animate-pulse rounded-xl bg-white/5" />;
  const upcoming = items.filter((a) => a.is_upcoming);
  const completed = items.filter((a) => !a.is_upcoming);

  return (
    <section className="space-y-4 rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="admin-forum-activities">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg text-white">Activities ({items.length})</h3>
        <span className="text-xs text-emerald-100/60">Dates in the past are moved to Completed automatically. Photos / videos can be added on completed items.</span>
      </div>

      <div className="space-y-3 rounded-2xl border border-emerald-300/20 bg-black/30 p-4">
        <p className="text-sm font-medium text-emerald-100">Add a new activity</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input type="date" data-testid="activity-new-date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="bg-black/20" />
          <Input data-testid="activity-new-title-en" placeholder="Title (English)" value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} className="bg-black/20" />
          <Input data-testid="activity-new-title-ml" placeholder="തലക്കെട്ട് (മലയാളം)" value={draft.title_ml} onChange={(e) => setDraft({ ...draft, title_ml: e.target.value })} className="bg-black/20" />
          <div />
          <Textarea data-testid="activity-new-desc-en" rows={3} placeholder="Description (English)" value={draft.description_en} onChange={(e) => setDraft({ ...draft, description_en: e.target.value })} className="bg-black/20 sm:col-span-2" />
          <Textarea data-testid="activity-new-desc-ml" rows={3} placeholder="വിവരണം (മലയാളം)" value={draft.description_ml} onChange={(e) => setDraft({ ...draft, description_ml: e.target.value })} className="bg-black/20 sm:col-span-2" />
        </div>
        <Button onClick={create} disabled={creating} data-testid="activity-new-create" className="rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add activity</>}
        </Button>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium text-emerald-100">Upcoming ({upcoming.length})</h4>
        {upcoming.length === 0 && <p className="text-xs text-emerald-100/60" data-testid="activities-admin-upcoming-empty">No upcoming activities.</p>}
        {upcoming.map((a) => <ActivityRow key={a.id} a={a} onSaved={onSaved} onDeleted={onDeleted} />)}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium text-amber-100">Completed ({completed.length})</h4>
        {completed.length === 0 && <p className="text-xs text-emerald-100/60" data-testid="activities-admin-completed-empty">No completed activities yet.</p>}
        {completed.map((a) => <ActivityRow key={a.id} a={a} onSaved={onSaved} onDeleted={onDeleted} />)}
      </div>
    </section>
  );
}
